(() => {
  const ATTRIBUTION_FIELDS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'gclid'
  ];
  const STORAGE_KEY = 'supplydoc:rayscan-financing-attribution';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function publishEvent(name, parameters = {}) {
    const detail = { ...parameters };
    document.dispatchEvent(new CustomEvent(`supplydoc:${name}`, { detail }));
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...detail });
  }

  function readAttribution() {
    let stored = {};
    try {
      stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
    } catch {
      stored = {};
    }

    const search = new URLSearchParams(window.location.search);
    const attribution = {
      ...stored,
      landing_page_url: stored.landing_page_url || window.location.href,
      referring_url: stored.referring_url || document.referrer
    };

    ATTRIBUTION_FIELDS.forEach((field) => {
      const value = search.get(field);
      if (value) attribution[field] = value.slice(0, 500);
    });

    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    } catch {
      // Private browsing or strict storage settings can make sessionStorage unavailable.
    }
    return attribution;
  }

  function fillAttribution(form, attribution) {
    form.querySelectorAll('[data-rayscan-attribution]').forEach((field) => {
      const key = field.dataset.rayscanAttribution;
      field.value = attribution[key] || '';
    });
  }

  function initializeSection(section) {
    if (section.dataset.rayscanInitialized === 'true') return;
    const form = section.querySelector('[data-rayscan-form]');
    const formCard = section.querySelector('[data-rayscan-form-card]');
    if (!form || !formCard) return;

    section.dataset.rayscanInitialized = 'true';
    const submitButton = form.querySelector('[data-rayscan-submit]');
    const status = form.querySelector('[data-rayscan-form-status]');
    const productContext = form.querySelector('[data-rayscan-product-context]');
    const desktopQuery = window.matchMedia('(min-width: 990px)');
    const attribution = readAttribution();
    let submitting = false;

    fillAttribution(form, attribution);

    function errorElementFor(name) {
      return Array.from(form.querySelectorAll('[data-error-for]'))
        .find((element) => element.dataset.errorFor === name);
    }

    function controlFor(name) {
      if (name === 'equipment_interest') return form.querySelector('.rayscan-form__interests');
      return Array.from(form.elements).find((element) => element.name === name);
    }

    function clearErrors() {
      form.querySelectorAll('[data-error-for]').forEach((element) => {
        element.textContent = '';
        element.hidden = true;
      });
      form.querySelectorAll('[aria-invalid="true"]').forEach((element) => element.removeAttribute('aria-invalid'));
    }

    function showFieldError(name, message) {
      const error = errorElementFor(name);
      const control = controlFor(name);
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
      if (control) control.setAttribute('aria-invalid', 'true');
    }

    function setStatus(message, type) {
      status.textContent = message;
      status.classList.toggle('is-success', type === 'success');
      status.hidden = !message;
      if (message) status.focus({ preventScroll: true });
    }

    function selectedEquipment() {
      return Array.from(form.querySelectorAll('[data-rayscan-interest]:checked'))
        .map((input) => input.value);
    }

    function updateSelectedContext() {
      if (!productContext) return;
      productContext.value = selectedEquipment().join(' | ');
    }

    function updateFormatValidity(control) {
      if (!control || !control.setCustomValidity) return;
      const value = String(control.value || '').trim();
      control.setCustomValidity('');

      if (control.name === 'phone' && value) {
        const digits = value.replace(/\D/gu, '');
        const validDigitCount = digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
        const usesValidCharacters = /^[0-9()+.\-\s]+$/u.test(value);
        if (!validDigitCount || !usesValidCharacters) {
          control.setCustomValidity('Enter a 10-digit U.S. phone number, such as (555) 555-0123.');
        }
      }

      if (control.name === 'zip_code' && value && !/^[0-9]{5}(?:-[0-9]{4})?$/u.test(value)) {
        control.setCustomValidity('Enter a 5-digit ZIP code or ZIP+4, such as 97201 or 97201-1234.');
      }
    }

    function validateForm() {
      clearErrors();
      const errors = [];

      Array.from(form.elements).forEach((control) => {
        updateFormatValidity(control);
        if (!control.matches('input:not([type="checkbox"]), select, textarea')) return;
        if (control.disabled || control.type === 'hidden' || control.validity.valid) return;
        const message = control.validationMessage || 'Complete this field.';
        showFieldError(control.name, message);
        errors.push({ control, message });
      });

      if (selectedEquipment().length === 0) {
        const message = 'Select at least one equipment option.';
        showFieldError('equipment_interest', message);
        errors.push({ control: controlFor('equipment_interest'), message });
      }

      if (errors.length) {
        setStatus('Please review the highlighted information and try again.', 'error');
        const firstControl = errors[0].control;
        if (firstControl) firstControl.focus({ preventScroll: false });
        return false;
      }
      setStatus('', '');
      return true;
    }

    function nextIncompleteControl() {
      const controls = Array.from(form.querySelectorAll('input, select, textarea'));
      return controls.find((control) => {
        if (control.disabled || control.type === 'hidden' || control.type === 'checkbox') return false;
        return control.required && !String(control.value || '').trim();
      }) || controls.find((control) => (
        !control.disabled &&
        control.type !== 'hidden' &&
        control.type !== 'checkbox' &&
        control.name !== 'website'
      ));
    }

    function moveToForm(trigger) {
      const interestId = trigger.dataset.interestId;
      const productName = trigger.dataset.productName;
      const interest = (interestId && document.getElementById(interestId)) || Array.from(form.querySelectorAll('[data-rayscan-interest]'))
        .find((input) => input.value === productName);
      if (interest) interest.checked = true;
      updateSelectedContext();
      publishEvent('rayscan_financing_cta', {
        cta_location: trigger.dataset.ctaLocation || 'unknown',
        product_name: trigger.dataset.productName || ''
      });

      formCard.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start'
      });
      window.setTimeout(() => {
        const nextControl = nextIncompleteControl();
        (nextControl || formCard).focus({ preventScroll: true });
      }, prefersReducedMotion() ? 0 : 350);
    }

    section.querySelectorAll('[data-rayscan-financing-cta]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        moveToForm(trigger);
      });
    });

    section.querySelectorAll('[data-rayscan-phone]').forEach((link) => {
      link.addEventListener('click', () => {
        publishEvent('rayscan_phone_click', {
          cta_location: link.dataset.ctaLocation || 'unknown'
        });
      });
    });

    section.querySelectorAll('[data-rayscan-product-view]').forEach((link) => {
      link.addEventListener('click', () => {
        publishEvent('rayscan_product_view', {
          product_name: link.dataset.productName || '',
          cta_location: 'product_card'
        });
      });
    });

    form.querySelectorAll('[data-rayscan-interest]').forEach((input) => {
      input.addEventListener('change', () => {
        updateSelectedContext();
        const group = controlFor('equipment_interest');
        const error = errorElementFor('equipment_interest');
        if (selectedEquipment().length && group) group.removeAttribute('aria-invalid');
        if (selectedEquipment().length && error) {
          error.textContent = '';
          error.hidden = true;
        }
      });
    });

    form.addEventListener('input', (event) => {
      if (!event.target.name || event.target.type === 'checkbox') return;
      updateFormatValidity(event.target);
      if (!event.target.validity.valid) return;
      event.target.removeAttribute('aria-invalid');
      const error = errorElementFor(event.target.name);
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
    });

    if (section.classList.contains('rayscan-financing--sticky')) {
      let stickyFrame;
      const updateStickyOffset = () => {
        stickyFrame = undefined;
        if (!desktopQuery.matches) {
          formCard.style.removeProperty('--rayscan-sticky-top');
          return;
        }
        const rootStyles = getComputedStyle(document.documentElement);
        const headerHeight = parseFloat(rootStyles.getPropertyValue('--header-height')) || 0;
        const settingOffset = Number(section.dataset.stickyOffset) || 0;
        const bottomMargin = 24;
        const topOffset = headerHeight + settingOffset;
        const bottomAlignedOffset = window.innerHeight - formCard.offsetHeight - bottomMargin;
        formCard.style.setProperty('--rayscan-sticky-top', `${Math.min(topOffset, bottomAlignedOffset)}px`);
      };
      const queueStickyUpdate = () => {
        if (stickyFrame) return;
        stickyFrame = window.requestAnimationFrame(updateStickyOffset);
      };
      updateStickyOffset();
      window.addEventListener('resize', queueStickyUpdate);
      if (window.ResizeObserver) new ResizeObserver(queueStickyUpdate).observe(formCard);
    }

    const mobileBar = section.querySelector('[data-rayscan-mobile-bar]');
    if (mobileBar && !section.classList.contains('rayscan-financing--design-mode')) {
      const updateMobileBar = (visible) => {
        mobileBar.classList.toggle('is-visible', visible);
        mobileBar.setAttribute('aria-hidden', String(!visible));
        mobileBar.querySelectorAll('a').forEach((link) => {
          link.tabIndex = visible ? 0 : -1;
        });
      };
      updateMobileBar(false);
      if (window.IntersectionObserver) {
        new IntersectionObserver((entries) => {
          updateMobileBar(entries.some((entry) => entry.isIntersecting));
        }, { rootMargin: '0px 0px -72px 0px' }).observe(section);
      } else {
        updateMobileBar(true);
      }
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (submitting || !validateForm()) return;

      const endpoint = form.dataset.endpoint;
      if (!endpoint) {
        setStatus(form.dataset.errorMessage, 'error');
        return;
      }

      submitting = true;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      updateSelectedContext();

      const formData = new FormData(form);
      const payload = Object.fromEntries(formData.entries());
      payload.equipment_interest = formData.getAll('equipment_interest');

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) {
          const fieldErrors = result.fields && typeof result.fields === 'object' ? result.fields : {};
          Object.entries(fieldErrors).forEach(([name, message]) => showFieldError(name, message));
          const summary = Object.values(fieldErrors).filter(Boolean).join(' ');
          throw new Error(summary || result.error || form.dataset.errorMessage);
        }

        if (window.location.pathname === '/pages/ray-america-financing') {
          const leadId = result.lead_id || result.id;

          if (
            window.Shopify &&
            Shopify.analytics &&
            typeof Shopify.analytics.publish === 'function'
          ) {
            Shopify.analytics.publish('ray_america_financing_lead', {
              form: 'ray_america_financing',
              lead_status: 'accepted',
              ...(leadId ? { lead_id: String(leadId) } : {})
            });
          }

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'ray_america_financing_lead',
            ...(leadId ? { lead_id: String(leadId) } : {})
          });
        }

        form.reset();
        fillAttribution(form, attribution);
        updateSelectedContext();
        const turnstileWidget = form.querySelector('.cf-turnstile');
        if (turnstileWidget && window.turnstile) window.turnstile.reset(turnstileWidget);
        clearErrors();
        setStatus(form.dataset.successMessage, 'success');
      } catch (error) {
        setStatus(error.message || form.dataset.errorMessage, 'error');
      } finally {
        submitting = false;
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
      }
    });
  }

  function initializeAll(root = document) {
    if (root.matches && root.matches('[data-rayscan-financing]')) initializeSection(root);
    root.querySelectorAll('[data-rayscan-financing]').forEach(initializeSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
